'use client';
import React from 'react';
import Image from 'next/image';
import { NewsCardObject } from '@/lib/types';
import { trackClick } from '@/lib/analytics';

// Only the fields the share handlers read. The caller (VideoCard, a Server
// Component) used to pass the whole NewsCardObject, which Next serializes
// into the RSC payload of every video page purely for these five values.
export type ShareCard = Pick<NewsCardObject, 'id' | 'title' | 'share_text' | 'share_image'> & {
  data?: Pick<NewsCardObject['data'], 'share_image_url'>;
};

interface ShareBtnProps {
  cardObj?: ShareCard;
  shareUrlOnly?: boolean;
  shareUrl?: string;
  shareLabel?: string;
}

const ShareBtns: React.FC<ShareBtnProps> = ({
  cardObj,
  shareUrlOnly = false,
  shareUrl,
  shareLabel,
}) => {
  const resolvedUrl =
    shareUrl ||
    (typeof window !== 'undefined' ? window.location.href : '');

  const onShareClick = React.useCallback(() => {
    if (!cardObj && !shareUrlOnly) return;

    const card = cardObj;
    const title = shareUrlOnly ? shareLabel || 'Share' : card?.title || '';
    const share_text = shareUrlOnly
      ? resolvedUrl
      : card?.share_text?.replace(/'/gi, "\\'") || '';

    trackClick('share_button_click', undefined, undefined, 'Share Button Clicked', {
      cardId: card?.id || 'generic',
    });

    if (navigator.share !== undefined) {
      navigator.share({
        text: share_text,
        title,
      });
    } else {
      window.open(`whatsapp://send?text=${encodeURIComponent(share_text)}`, '_blank');
    }
  }, [cardObj, shareUrlOnly, resolvedUrl, shareLabel]);

  const onWhatsAppClick = React.useCallback(() => {
    if (!cardObj && !shareUrlOnly) return;

    trackClick('whatsapp_share_click', undefined, undefined, 'WhatsApp Share Clicked', {
      cardId: cardObj?.id || 'generic',
    });

    const text = shareUrlOnly
      ? encodeURIComponent(resolvedUrl)
      : encodeURIComponent(
          `${cardObj?.share_text || ''} ${cardObj?.share_image || cardObj?.data?.share_image_url || ''}`,
        );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  }, [cardObj, shareUrlOnly, resolvedUrl]);

  return <div className='inline-flex grid-cols-2 gap-x-2'>
    <button className="flex items-center space-x-1" onClick={onShareClick}>
      {shareUrlOnly == true ? (
        <Image
          alt='Share'
          width={32}
          height={32}
          src='https://media.inshorts.com/inshorts/images/v1/variants/png/m/2026/04_apr/20_mon/img_1776671927584_25.png'
        />
      ) : (
        <Image
          alt='Share'
          width={20}
          height={20}
          src='/_assets/icons/share.svg'
          className='h-[20px] w-[20px]'
        />
      )}
    </button>
    <button className="flex items-center space-x-1" onClick={onWhatsAppClick}>
      {shareUrlOnly == true ? (
        <Image
          alt='Share on WhatsApp'
          width={32}
          height={32}
          src='https://media.inshorts.com/inshorts/images/v1/variants/png/m/2026/04_apr/20_mon/img_1776671781476_530.png'
        />
      ) : (
        <Image alt="Share on WhatsApp" width={24} height={24} src="/_assets/icons/whatsapp.svg" className='h-[24px] w-[24px]' />
      )}
    </button>
  </div>
};

export default ShareBtns